import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';

/**
 * Configuração base da API
 */
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const API_TIMEOUT = 30000; // 30 segundos
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 segundo

// Configurações específicas para compliance
const COMPLIANCE_ENDPOINTS = [
  '/social/pages/privacy',
  '/social/pages/terms',
  '/social/pages/help',
  '/social/app-download',
  '/social/settings'
];

/**
 * Interface para resposta padrão da API
 */
export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  error?: string;
}

/**
 * Interface para resposta de página estática
 */
export interface StaticPageResponse {
  id: number;
  title: string;
  content: string;
  lastUpdated?: string;
  metaDescription?: string;
}

/**
 * Interface para dados de download do app
 */
export interface AppDownloadResponse {
  video_embed_url: string;
  download_button_text: string;
  download_url: string;
  instructions: string[];
}

/**
 * Interface para configuração de endpoint
 */
export interface EndpointConfig {
  retries?: number;
  timeout?: number;
  cacheable?: boolean;
  critical?: boolean;
}

/**
 * Interface para configuração de retry
 */
interface RetryConfig {
  retries?: number;
  retryDelay?: number;
  retryCondition?: (error: AxiosError) => boolean;
}

/**
 * Classe base para serviços de API
 * Fornece funcionalidades centralizadas para chamadas HTTP
 */
export class ApiService {
  protected axiosInstance: AxiosInstance;
  private requestInterceptorId?: number;
  private responseInterceptorId?: number;

  constructor(baseURL: string = API_BASE_URL) {
    this.axiosInstance = axios.create({
      baseURL: `${baseURL}/api`,
      timeout: API_TIMEOUT,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  /**
   * Configura interceptors para requisições e respostas
   */
  private setupInterceptors(): void {
    // Request interceptor
    this.requestInterceptorId = this.axiosInstance.interceptors.request.use(
      (config) => {
        // Adicionar token de autenticação se existir
        const token = this.getAuthToken();
        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.responseInterceptorId = this.axiosInstance.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        return this.handleError(error);
      }
    );
  }

  /**
   * Obtém token de autenticação do localStorage
   * CORREÇÃO: Verifica ambas as chaves para compatibilidade
   */
  private getAuthToken(): string | null {
    try {
      return localStorage.getItem('token') || localStorage.getItem('authToken');
    } catch (error) {
      return null;
    }
  }

  /**
   * Define token de autenticação
   * CORREÇÃO: Salva como 'token' para manter padrão do sistema legado
   */
  public setAuthToken(token: string): void {
    try {
      localStorage.setItem('token', token);
      // Opcional: manter authToken sincronizado se necessário futuramente
      localStorage.setItem('authToken', token);
    } catch (error) {
      // Silently fail
    }
  }

  /**
   * Remove token de autenticação
   * CORREÇÃO: Limpa ambas as chaves
   */
  public clearAuthToken(): void {
    try {
      localStorage.removeItem('token');
      localStorage.removeItem('authToken');
    } catch (error) {
      // Silently fail
    }
  }

  /**
   * Verifica se é um endpoint de compliance
   */
  private isComplianceEndpoint(url?: string): boolean {
    if (!url) return false;
    return COMPLIANCE_ENDPOINTS.some(endpoint => url.includes(endpoint));
  }

  /**
   * Tratamento centralizado de erros
   */
  private async handleError(error: AxiosError): Promise<never> {
    if (error.response) {
      const status = error.response.status;
      const data = error.response.data as any;
      const url = error.config?.url;

      const isCompliance = this.isComplianceEndpoint(url);

      switch (status) {
        case 401:
          // Não autorizado - limpar token e redirecionar para login
          this.clearAuthToken();
          if (typeof window !== 'undefined') {
            // CORREÇÃO: Redireciona para a rota correta de admin
            window.location.href = '/admin/login';
          }
          throw new Error('Sessão expirada. Por favor, faça login novamente.');

        case 403:
          if (isCompliance) {
            throw new Error('Acesso negado. Você não tem permissão para modificar conteúdo de compliance.');
          }
          throw new Error('Você não tem permissão para acessar este recurso.');

        case 404:
          if (isCompliance) {
            throw new Error('Página de compliance não encontrada. Contate o administrador.');
          }
          throw new Error('Recurso não encontrado.');

        case 422:
          if (isCompliance) {
            throw new Error(`Dados de compliance inválidos: ${data.message || 'Verifique todos os campos.'}`);
          }
          throw new Error(data.message || 'Dados inválidos.');

        case 429:
          if (isCompliance) {
            throw new Error('Muitas requisições de compliance. Por favor, aguarde antes de tentar novamente.');
          }
          throw new Error('Muitas requisições. Por favor, tente novamente mais tarde.');

        case 500:
        case 502:
        case 503:
        case 504:
          if (isCompliance) {
            throw new Error('Erro no servidor de compliance. A página pode estar temporariamente indisponível.');
          }
          throw new Error('Erro no servidor. Por favor, tente novamente mais tarde.');

        default:
          const errorMessage = data.message || data.error || 'Erro ao processar requisição.';
          if (isCompliance) {
            throw new Error(`Erro no sistema de compliance: ${errorMessage}`);
          }
          throw new Error(errorMessage);
      }
    } else if (error.request) {
      const isCompliance = this.isComplianceEndpoint(error.config?.url);

      if (isCompliance) {
        throw new Error('Erro de conexão com o sistema de compliance. Verifique sua internet e tente novamente.');
      }
      throw new Error('Erro de conexão. Verifique sua internet e tente novamente.');
    } else {
      throw new Error('Erro ao configurar requisição.');
    }
  }

  private async retryRequest<T>(
    requestFn: () => Promise<AxiosResponse<T>>,
    config: RetryConfig = {}
  ): Promise<AxiosResponse<T>> {
    const {
      retries = MAX_RETRIES,
      retryDelay = RETRY_DELAY,
      retryCondition = (error: AxiosError) => {
        return !error.response || (error.response.status >= 500 && error.response.status < 600);
      },
    } = config;

    let lastError: any;

    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        return await requestFn();
      } catch (error) {
        lastError = error;
        if (!retryCondition(error as AxiosError)) throw error;
        if (attempt === retries) throw error;
        const delay = retryDelay * Math.pow(2, attempt);
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
    throw lastError;
  }

  // Métodos públicos com tipagem corrigida
  public async get<T>(endpoint: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.retryRequest<ApiResponse<T>>(
      () => this.axiosInstance.get(endpoint, config)
    );
    // Suporta tanto { success: true, data: ... } quanto retorno direto
    return response.data.data !== undefined ? response.data.data : (response.data as any);
  }

  public async post<T>(endpoint: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.axiosInstance.post<ApiResponse<T>>(endpoint, data, config);
    return response.data.data !== undefined ? response.data.data : (response.data as any);
  }

  public async put<T>(endpoint: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.axiosInstance.put<ApiResponse<T>>(endpoint, data, config);
    return response.data.data !== undefined ? response.data.data : (response.data as any);
  }

  public async patch<T>(endpoint: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.axiosInstance.patch<ApiResponse<T>>(endpoint, data, config);
    return response.data.data !== undefined ? response.data.data : (response.data as any);
  }

  public async delete<T>(endpoint: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.axiosInstance.delete<ApiResponse<T>>(endpoint, config);
    return response.data.data !== undefined ? response.data.data : (response.data as any);
  }

  public removeInterceptors(): void {
    if (this.requestInterceptorId !== undefined) {
      this.axiosInstance.interceptors.request.eject(this.requestInterceptorId);
    }
    if (this.responseInterceptorId !== undefined) {
      this.axiosInstance.interceptors.response.eject(this.responseInterceptorId);
    }
  }

  public async getCompliancePage<T = StaticPageResponse>(endpoint: string): Promise<T> {
    return this.get<T>(endpoint);
  }

  public async updateComplianceSettings<T = any>(endpoint: string, data: any): Promise<T> {
    return this.put<T>(endpoint, data);
  }

  public async getAppDownloadData(): Promise<AppDownloadResponse> {
    return this.getCompliancePage<AppDownloadResponse>('/social/app-download');
  }

  public async preloadComplianceData(): Promise<{
    settings: any | null;
    privacyPage: StaticPageResponse | null;
    termsPage: StaticPageResponse | null;
    helpPage: StaticPageResponse | null;
    appDownload: AppDownloadResponse | null;
  }> {
    try {
      const results = await Promise.allSettled([
        this.get('/social/settings'),
        this.getCompliancePage('/social/pages/privacy'),
        this.getCompliancePage('/social/pages/terms'),
        this.getCompliancePage('/social/pages/help'),
        this.getAppDownloadData(),
      ]);

      return {
        settings: results[0].status === 'fulfilled' ? results[0].value : null,
        privacyPage: results[1].status === 'fulfilled' ? results[1].value : null,
        termsPage: results[2].status === 'fulfilled' ? results[2].value : null,
        helpPage: results[3].status === 'fulfilled' ? results[3].value : null,
        appDownload: results[4].status === 'fulfilled' ? results[4].value : null,
      };
    } catch (error) {
      throw error;
    }
  }

  public async checkComplianceHealth(): Promise<{
    healthy: boolean;
    endpoints: Record<string, boolean>;
    details: string[];
  }> {
    const endpoints = [
      '/social/settings',
      '/social/pages/privacy',
      '/social/pages/terms',
      '/social/pages/help',
    ];

    const results = await Promise.allSettled(
      endpoints.map(endpoint => this.get(endpoint).then(() => ({ endpoint, status: 'ok' })))
    );

    const healthy = results.every(result => result.status === 'fulfilled');
    const endpointStatus: Record<string, boolean> = {};
    const details: string[] = [];

    results.forEach((result, index) => {
      const endpoint = endpoints[index];
      if (result.status === 'fulfilled') {
        endpointStatus[endpoint] = true;
        details.push(`✅ ${endpoint} - OK`);
      } else {
        endpointStatus[endpoint] = false;
        details.push(`❌ ${endpoint} - ERRO: ${result.reason.message}`);
      }
    });

    return { healthy, endpoints: endpointStatus, details };
  }
}

export const apiService = new ApiService();
export { axios };
export const API_BASE_URL_EXPORT = API_BASE_URL;